/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import {
    call, put, select, fork, take, takeEvery,
} from 'redux-saga/effects';

import mbpLogger from 'mbp-logger';
import Cookies from 'universal-cookie';
import mbpUtil from 'mbp-api-util';
import { trackEvent } from '../../../../TagManager/ducks/TagManager/TagManager-Actions';
// ../../../../TagManager
import { showLoadingMessageActions } from '../../../../Common/ducks/ShowLoadingMessage';

import { calculateCartComplete } from '../../../../Checkout/ducks/Checkout/Checkout-Actions';

import { checkJWT } from '../../../../Checkout/ducks/Global/Global-Operations';
import { retrieveCart } from '../../Cart/ducks/RetrieveCart/RetrieveCart-Operations';

import {
    getOrderId, getPaymentMethod,
    getIfGiftItemHaveDeliveryAddresssAndDate,
} from '../../../../Checkout/ducks/Common/Common-Selectors';

import cartServiceClient from '../../../../../../apis/cart-service-apis/cartServiceClient';

import { getBannerCodesAttempted } from './Promotion-Selectors';
import {
    promotionRemove,
    applyPromotion,
    promotionSuccess,
    clearPromotionData,
    setMfeBannerCodesAttempted,
    mfeRedirectToPromoAffiliate,
    mfeApplyPromotionIsLoading,
} from './Promotion-Actions';
import { workerCalculateCart } from '../../../../Checkout/ducks/Checkout/Checkout-Operations';
import { processFeatureErrors } from '../../../../Checkout/ducks/Common/ducks/Error/Error-Operations';
import { clearOrderItemErrors } from '../../../../Checkout/ducks/Common/ducks/Error/Error-Actions';
import { prepareRetreiveCart } from '../../Cart/ducks/RetrieveCart/PrepareRetreiveCart-Operations';
import { getAnalyticsStorageData } from '../../../../../../app/helpers/tracking/session/sessionTrackingHelpers';
import { getUserEmailId } from '../../../../Member/ducks/Common/Common-Selectors';
import { getFeatureFlag, getOfferGatewayData } from '../../App/App-Selectors';
import { encodeURLXTimes } from '../../App/App-Helper';
import { flagOffShowLoadingMessage, updateShowLoadingMessage } from '../../../../Common/ducks/ShowLoadingMessage/ShowLoadingMessage-Actions';

/**
Worker for Apply promotion
@param {action} data to apply the promotion code to cart
@returns {object} is set into Redux for ui component have access the information
*/

function* workerApplyPromotionRequiringAuthentication(payload) {
    const { promoAffiliate } = payload;
    const isPromoRequiringAuthenticationEnabled = yield select(getFeatureFlag('is-promo-code-requiring-authentication-enabled'));
    const { active, bannerCode } = isPromoRequiringAuthenticationEnabled[promoAffiliate];
    const affiliateURL = '';
    const redirectPayload = {
        promoAffiliate,
        affiliateURL,
    };

    // if feature flag is off for all affiliate partners, do not continue
    if (!isPromoRequiringAuthenticationEnabled['all']?.active) {
        return;
    }

    if (active && promoAffiliate === 'usaa') {
        // APP_USAA_SAML_URL UAT example:
        // https://rwww.usaa.com/utils/proof-of-immigration?redirect=1800FLOWERS_DIR&extraParams=
        // USAA requires the url to be encoded 3 times
        redirectPayload.affiliateURL = `${mbpUtil.getEnv('APP_USAA_SAML_URL')}${encodeURLXTimes(`${window?.location?.href}?r=${bannerCode}`, 3)}`;
        yield put(mfeRedirectToPromoAffiliate(redirectPayload));
    }
}

function* workerApplyPromotion(action) {
    const bannerCookie = new Cookies().get('banner') || {};
    const applyPromoCode = action.payload?.promotionCode || '';
    const applyMembershipId = action.payload?.membershipId || '';
    const pageType = action.payload?.pageType || '';
    const history = action.payload?.history || '';
    const attributes = action.payload?.attributes || [];
    const reason = action.payload?.reason || '';

    const cartId = yield select(getOrderId);

    try {
        yield put(updateShowLoadingMessage());

        // If reason is 'attribution', then this promo application call is from promo auto-apply logic
        if (reason === 'attribution') {
            // To avoid calling the heavy calculatecart API during an auto-apply attempt, pass calcode SHIPPING for a lightweight call to calculate shipping
            yield fork(workerCalculateCart, { calcode: 'SHIPPING' });
            yield take(calculateCartComplete().type);
        } else if (pageType === 'payment') {
            // For manual promo application, call the full calculatecart API
            yield fork(workerCalculateCart);
            yield take(calculateCartComplete().type);
        }

        yield put(clearOrderItemErrors(
            cartId,
            'promotion',
            { errType: 'clearAllFields', field: '' },
        ));

        if (cartId) {
            // Prepare
            const jwtToken = yield call(checkJWT);
            const selectedPaymentMethod = yield select(getPaymentMethod);
            let email = yield select(getUserEmailId) || '';
            const offerGatewayData = yield select(getOfferGatewayData) || {};
            const { offerGatewayOptin } = offerGatewayData || {};
            const currentPayment = selectedPaymentMethod && selectedPaymentMethod.id ? selectedPaymentMethod.id : '';

            // Apply Promo
            if (applyPromoCode.indexOf('USAA') >= 0) {
                if (bannerCookie.c) {
                    attributes.push({
                        attributeName: 'bannerCode',
                        attributeValue: bannerCookie?.c,
                    });
                }

                const analyticsSession = getAnalyticsStorageData();

                if ((!bannerCookie.c || bannerCookie.c === 'null') && analyticsSession?.landingParams?.r) {
                    attributes.push({
                        attributeName: 'bannerCode',
                        attributeValue: analyticsSession.landingParams.r,
                    });
                }
            }

            if (offerGatewayOptin) {
                email = offerGatewayData.email;
            }

            const payload = {
                env: {},
                jwtToken,
                cartId,
                billingZipCode: currentPayment,
                promotionCode: applyPromoCode,
                membershipCode: applyMembershipId,
                attributes,
                email,
            };

            const res = yield call(cartServiceClient.applyPromotion, payload);
            yield put(clearPromotionData());

            // Tracker
            const eventAction = 'CO_Apply_Promo_Succeeded';
            const promoEventObj = {
                eventCategory: 'Checkout',
                eventAction,
                eventLabel: res,
                coupon: applyPromoCode,
            };
            yield put(trackEvent(promoEventObj));
            if (reason !== 'attribution') {
                yield call(prepareRetreiveCart, pageType);

                yield call(retrieveCart);

                if (history?.location?.pathname?.includes('/checkout/payment')
                    || history?.location?.pathname?.includes('/checkout/review-order')) {
                    const missingAddressDateAndMessage = yield select(getIfGiftItemHaveDeliveryAddresssAndDate);
                    if (!missingAddressDateAndMessage) {
                        history.push(`/checkout/shipping/${cartId}`);
                    }
                }
                yield put(setMfeBannerCodesAttempted([]));
            }
        } else {
            const bannerCodesAttempted = yield select(getBannerCodesAttempted);
            yield put(setMfeBannerCodesAttempted([...bannerCodesAttempted, { promoCode: applyPromoCode, membershipId: applyMembershipId }]));
        }
    } catch (ex) {
        mbpLogger.logError({
            promoCode: applyPromoCode,
            membershipId: applyMembershipId,
            appName: process.env.npm_package_name,
            module: 'Promotion-Operations',
            function: 'workerApplyPromotion',
            jsError: ex,
            errorMessage: bannerCookie,
            message: `workerApplyPromotion - Apply promotion could not be applied - ${cartId}`,
        });
        const eventAction = 'CO_Apply_Promo_Failed';
        const promoEventObj = {
            eventCategory: 'Checkout',
            eventAction,
            eventLabel: ex,
            coupon: applyPromoCode,
        };

        // Retrieve cart to get the latest cart data if calculate cart was called previously
        if (reason === 'attribution' || pageType === 'payment') {
            try {
                yield call(retrieveCart);
            } catch (cartError) {
                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    module: 'Promotion-Operations',
                    function: 'workerApplyPromotion',
                    jsError: cartError,
                    message: `Failed to retrieve cart after promo application error - ${cartId}`,
                });
            }
        }

        yield put(trackEvent(promoEventObj));

        yield fork(processFeatureErrors, {
            data: {
                orderItemId: cartId,
                errorFeatureType: 'promotion',
                errorValues: ex.response?.data,
            },
        });

        if (ex.response?.data.errorKey === 'NOT_AUTHORIZED' || ex.response?.data.errorKey === 'Not Authorized to use this promotion') {
            yield call(workerApplyPromotionRequiringAuthentication, { promoAffiliate: 'usaa' });
        }

        const bannerCodesAttempted = yield select(getBannerCodesAttempted);

        yield put(setMfeBannerCodesAttempted([...bannerCodesAttempted, { promoCode: applyPromoCode, membershipId: applyMembershipId }]));
    }
    yield put(promotionSuccess());
    yield put(flagOffShowLoadingMessage());
}

/**
Worker for Remove promotion
@param {action} data to remove the promotion code from cart
@returns {object} is set into Redux for ui component have access the information
*/

function* workerRemovePromotion(action) {
    const promoCode = action?.payload?.promoCode || '';
    const cartId = yield select(getOrderId);

    try {
        const pageType = action?.payload?.pageType || '';
        const callbackFn = action.payload.callbackFn;

        yield put(clearOrderItemErrors(
            cartId,
            'promotion',
            { errType: 'clearAllFields', field: '' },
        ));

        if (cartId) {
            yield put(mfeApplyPromotionIsLoading(true));
            const JWT_TOKEN = yield call(checkJWT);
            const res = yield call(cartServiceClient.removePromotion,
                {
                    env: {},
                    jwtToken: JWT_TOKEN,
                    cartId,
                    promotionCode: promoCode,
                });

            const eventAction = 'CO_Remove_Promo_Succeeded';
            const promoEventObj = {
                eventCategory: 'Checkout',
                eventAction,
                eventLabel: res,
            };
            if (callbackFn) {
                yield call(callbackFn, res);
            }
            yield put(trackEvent(promoEventObj));

            yield call(prepareRetreiveCart, pageType);

            yield call(retrieveCart);
        } else {
            const windowLocation = (typeof window !== 'undefined') ? window.location.href : '';
            mbpLogger.logError({
                promoCode,
                appName: process.env.npm_package_name,
                module: 'Promotion-Operations.js',
                function: 'workerRemovePromotion',
                message: 'workerRemovePromotion - Try to remove promotion code without cartId',
                windowLocation,
            });
        }
    } catch (exp) {
        mbpLogger.logError({
            promoCode,
            appName: process.env.npm_package_name,
            module: 'Promotion-Operations.js',
            function: 'workerRemovePromotion',
            jsError: exp,
            message: 'workerRemovePromotion -  Remove promotioncode failed ',
        });

        const eventAction = 'CO_Remove_Promo_Failed';
        const promoEventObj = {
            eventCategory: 'Checkout',
            eventAction,
            eventLabel: exp,
        };

        yield put(trackEvent(promoEventObj));
        yield put(showLoadingMessageActions.flagOffShowLoadingMessage());
        yield fork(processFeatureErrors, {
            data: {
                orderItemId: cartId,
                errorFeatureType: 'promotion',
                errorValues: exp.response?.data,
            },
        });
    }
    yield put(mfeApplyPromotionIsLoading(false));
    yield put(promotionSuccess());
}

function* watchApplyPromotion() {
    yield takeEvery(applyPromotion().type, workerApplyPromotion);
}

function* watchRemovePromotion() {
    yield takeEvery(promotionRemove().type, workerRemovePromotion);
}

const watchers = [
    fork(watchApplyPromotion),
    fork(watchRemovePromotion),
];

export {
    watchers,
    workerApplyPromotion,
};

export default {};
